import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listAllEmployees, peopleGateway } from '../../people';
import { employmentStatusLabels, type CompanyWorkLocationOption, type EmployeeResponse, type JobTitleOption } from '../../people/types';
import { compensationGateway } from '../../compensation';
import { compensationPayTypeLabels, type WorkRateAssignmentResponse } from '../../compensation/types';
import { payConfigGateway } from '../../pay-config';
import { workRateAmountBasisLabels, type DeductionCodeResponse, type EarningCodeResponse, type WorkRateResponse } from '../../pay-config/types';
import { customPayRunGateway } from '../index';
import { MEMO_MAX_LENGTH, RUN_DISCARDED, RUN_WITHDRAWN, runStatusLabel, type DeductionElectionWithOptOutResponse, type DuplicateFlag, type EarningLineInput, type EmployeeSelectionInput } from '../types';
import { DuplicateFlagsNotice } from './DuplicateFlagsNotice';
import type { StepProps } from './CustomPayRunWizard';
import { NotConnected } from './NotConnected';
import { formatUsDate, formatUsPeriod, frequencyLabel, usePayScheduleInfo } from './runContext';
import { describeEarningLine, earningLineAmount } from './usePayrollRecords';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { CPR_PERMISSIONS, NO_PERMISSION_NOTE } from '../permissions';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { describeExceptionProblem, describeServiceLineProblem, type ServiceLineProblem } from '../../healthcare-rates/healthcareErrors';
import { formatWireMoney } from '../../healthcare-rates/money';
import { HdcErrorCode, SYNTHETIC_RATES_NOTICE, SYNTHETIC_RATES_NOTICE_TITLE, rateSourceLabels, type EarningDetail } from '../../healthcare-rates/types';
import { healthcareRatesGateway } from '../../healthcare-rates/index';
import { ServiceLineForm } from './ServiceLineForm';

const HANDED_OFF = 'HandedOffToPayrollReview';
const CALCULATED_STATUSES = ['Calculated', 'ExceptionsPresent', 'ReadyForReview'];
const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

type DraftLine = EarningLineInput & { key: string };

const normalizeLines = (lines: EarningLineInput[]) =>
  JSON.stringify(lines.map((l) => [l.earningCodeId, l.label, l.hours, l.rate, l.flatAmount, serviceLineKey(l)]));

/** A service line's own inputs, for telling whether it changed (the server's snapshots are not an input). Null on every other line, so older lines compare exactly as before. */
function serviceLineKey(l: EarningLineInput) {
  const s = l.serviceLine;
  return s ? [s.disciplineId, s.serviceTypeId, s.payBasis, s.quantity, s.workedHours, s.serviceDate, s.workLocationId, s.additionalEarningAuthorized, s.additionalEarningReason] : null;
}

/** The employee's pay-rate assignment in force on the run's check date (same date window as the pay-rate screen: starts on or
 * before the date, not ended on or before it). The primary rate wins; otherwise the most recently started. */
function activeAssignmentOn(assignments: WorkRateAssignmentResponse[], asOf: string): WorkRateAssignmentResponse | null {
  const active = assignments.filter((a) => a.effectiveStartDate <= asOf && (a.effectiveEndDate === null || a.effectiveEndDate > asOf));
  active.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || b.effectiveStartDate.localeCompare(a.effectiveStartDate));
  return active[0] ?? null;
}

type RateState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; assignment: WorkRateAssignmentResponse | null };

function fullName(e: { firstName: string; lastName: string }) {
  return `${e.firstName} ${e.lastName}`.trim();
}

/** Step 1 — Custom Payroll. Everything on this screen is a draft held in the page until "Save and Confirm All Changes" (or
 * "Proceed to Review Payroll", which saves first) writes it through the existing Custom Pay Run API: employee selection
 * (with the non-Active acknowledgment), run-scoped earning lines (real, active earning codes only) and per-run deduction
 * opt-outs. The pay period, pay day and schedule are fixed when the run is created, so they are shown read-only.
 * The run's memo (EPN-242) is saved with the rest, through its own endpoint. Leaves, reimbursements/mileage, Generate Paystub and Bulk Generate Paystubs have no backend yet: they are visible,
 * disabled and labelled "Not yet connected" — nothing behind them is saved. */
export function CustomPayrollStep({ run, refreshRun, goToStep }: StepProps) {
  const discarded = run.status === RUN_DISCARDED;
  const withdrawn = run.status === RUN_WITHDRAWN;
  const readOnly = run.status === HANDED_OFF || discarded || withdrawn;
  const can = usePermissionCheck();
  // Display only: the server re-checks custompayruns.write on every save. When the permission list is not known this is true, so nothing is hidden on a guess.
  const canWrite = can(CPR_PERMISSIONS.write);
  const isBonus = run.runType === 2;
  const { schedules, schedule } = usePayScheduleInfo(run);

  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [earningCodes, setEarningCodes] = useState<EarningCodeResponse[]>([]);
  const [deductionCodes, setDeductionCodes] = useState<DeductionCodeResponse[]>([]);
  const [jobTitles, setJobTitles] = useState<JobTitleOption[]>([]);
  const [locations, setLocations] = useState<CompanyWorkLocationOption[]>([]);

  // Saved (server) state and the draft the preparer is editing.
  const [savedSelections, setSavedSelections] = useState<Map<string, EmployeeSelectionInput>>(new Map());
  const [selections, setSelections] = useState<Map<string, EmployeeSelectionInput>>(new Map());
  const [savedEarnings, setSavedEarnings] = useState<Record<string, EarningLineInput[]>>({});
  const [earnings, setEarnings] = useState<Record<string, DraftLine[]>>({});
  const [savedDeductions, setSavedDeductions] = useState<DeductionElectionWithOptOutResponse[]>([]);
  const [deductions, setDeductions] = useState<DeductionElectionWithOptOutResponse[]>([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [branchByEmployee, setBranchByEmployee] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [rateByEmployee, setRateByEmployee] = useState<Record<string, RateState>>({});
  const [workRates, setWorkRates] = useState<WorkRateResponse[]>([]);
  const defaultsApplied = useRef(false);
  const [editingKey, setEditingKey] = useState<string | 'new' | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'save' | 'proceed' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [duplicateFlags, setDuplicateFlags] = useState<DuplicateFlag[]>([]);
  // Healthcare service lines: a refused line is explained in plain language with its fix, and the price the last calculation gave each line is shown read-only.
  const [blocked, setBlocked] = useState<ServiceLineProblem | null>(null);
  const blockedRef = useRef<HTMLDivElement>(null);
  const [resolvedByEmployee, setResolvedByEmployee] = useState<Record<string, EarningDetail[]>>({});
  // The server saves a service line with ids only, so the names a line shows (the discipline in the Discipline/Role column, the service type in Earnings) come from the catalogs.
  const [disciplineNames, setDisciplineNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [serviceTypeNames, setServiceTypeNames] = useState<ReadonlyMap<string, string>>(new Map());

  // EPN-242: the run's memo (a draft held in the page until saved with the rest) and the rows ticked for a bulk action (never saved: they only drive Include / Exclude selected).
  const [savedMemo, setSavedMemo] = useState(run.description ?? '');
  const [memo, setMemo] = useState(run.description ?? '');
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // The picker needs every employee, not one page — the shared load-all helper follows each page.
      const [allEmployees, initialSelectionRows, lineRows, deductionRows, codes, dedCodes, titles, locs, rates, disciplines, serviceTypes] = await Promise.all([
        listAllEmployees(peopleGateway, { sortBy: 'name' }),
        customPayRunGateway.getEmployees(run.id),
        customPayRunGateway.getEarnings(run.id),
        customPayRunGateway.getDeductions(run.id),
        payConfigGateway.listEarningCodes(),
        payConfigGateway.listDeductionCodes().catch(() => [] as DeductionCodeResponse[]),
        peopleGateway.listJobTitles().catch(() => [] as JobTitleOption[]),
        peopleGateway.listCompanyWorkLocations().catch(() => [] as CompanyWorkLocationOption[]),
        payConfigGateway.listWorkRates().catch(() => [] as WorkRateResponse[]),
        healthcareRatesGateway.listDisciplines({ includeInactive: true }).catch(() => []),
        healthcareRatesGateway.listServiceTypes({ includeInactive: true }).catch(() => []),
      ]);
      setDisciplineNames(new Map(disciplines.map((d) => [d.id, d.name])));
      setServiceTypeNames(new Map(serviceTypes.map((t) => [t.id, t.name])));
      setEmployees(allEmployees);
      setEarningCodes(codes.filter((c) => c.isActive));
      setDeductionCodes(dedCodes);
      setJobTitles(titles);
      setLocations(locs);
      setWorkRates(rates);

      // Active employees are included by default when a run is opened for the first time (it has no selection rows yet).
      // Once the run has any saved selection — included or not — it is never defaulted again, so reopening or reloading
      // keeps exactly what was saved and never creates duplicates (the API replaces the run's set in one write). A run
      // handed to review always has selections, so this never writes to a read-only run.
      let selectionRows = initialSelectionRows;
      let defaultsNotSaved = false;
      if (initialSelectionRows.length === 0 && allEmployees.length > 0 && !defaultsApplied.current) {
        defaultsApplied.current = true;
        const defaults: EmployeeSelectionInput[] = allEmployees.map((e) => ({ employeeId: e.id, isIncluded: !isBonus && e.status === 0, acknowledgeStatusWarning: false })); // a Bonus run starts empty: nobody is ticked until the preparer chooses
        try {
          selectionRows = await customPayRunGateway.setEmployees(run.id, defaults);
        } catch {
          // Could not save the defaults: keep them as an unsaved draft the preparer can still save or change.
          defaultsNotSaved = true;
          selectionRows = defaults.map((d) => ({
            employeeId: d.employeeId, employeeNumber: '', firstName: '', lastName: '', employmentStatus: '',
            isIncluded: d.isIncluded, acknowledgedStatusWarning: false, requiresAcknowledgment: false,
          }));
        }
      }

      const map = new Map<string, EmployeeSelectionInput>();
      for (const s of selectionRows) {
        map.set(s.employeeId, { employeeId: s.employeeId, isIncluded: s.isIncluded, acknowledgeStatusWarning: s.acknowledgedStatusWarning });
      }
      setSavedSelections(defaultsNotSaved ? new Map() : map);
      setSelections(new Map(map));

      const byEmployee: Record<string, EarningLineInput[]> = {};
      for (const l of lineRows) {
        (byEmployee[l.employeeId] ??= []).push({ earningCodeId: l.earningCodeId, label: l.label, hours: l.hours, rate: l.rate, flatAmount: l.flatAmount, ...(l.serviceLine ? { serviceLine: l.serviceLine } : {}) });
      }
      setSavedEarnings(byEmployee);
      setEarnings(Object.fromEntries(Object.entries(byEmployee).map(([id, ls]) => [id, ls.map((l) => ({ ...l, key: crypto.randomUUID() }))])));

      setSavedDeductions(deductionRows);
      setDeductions(deductionRows);

      setSelectedId((prev) => prev ?? selectionRows.find((s) => s.isIncluded)?.employeeId ?? allEmployees[0]?.id ?? null);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load this run.'));
    } finally {
      setLoading(false);
    }
  }, [run.id, isBonus]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  // Branch = the employee's primary work location, read once per employee as they are selected. Best-effort context.
  useEffect(() => {
    if (!selectedId || branchByEmployee[selectedId] !== undefined) return;
    let cancelled = false;
    peopleGateway
      .getEmployeeWorkLocation(selectedId)
      .then((r) => {
        const name = locations.find((l) => l.id === r.primary?.workLocationId)?.name ?? '—';
        if (!cancelled) setBranchByEmployee((prev) => ({ ...prev, [selectedId]: name }));
      })
      .catch(() => {
        if (!cancelled) setBranchByEmployee((prev) => ({ ...prev, [selectedId]: '—' }));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, locations, branchByEmployee]);

  // The employee's saved pay rate as of the run's check date, read from Compensation (the same source as the pay-rate screen).
  useEffect(() => {
    if (!selectedId || rateByEmployee[selectedId] !== undefined) return;
    let cancelled = false;
    compensationGateway
      .listWorkRateAssignments(selectedId)
      .then((list) => {
        if (!cancelled) setRateByEmployee((prev) => ({ ...prev, [selectedId]: { status: 'ready', assignment: activeAssignmentOn(list, run.checkDate) } }));
      })
      .catch(() => {
        if (!cancelled) setRateByEmployee((prev) => ({ ...prev, [selectedId]: { status: 'error' } }));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, rateByEmployee, run.checkDate]);

  // The price the last calculation gave each service line (rate, where it came from, an explanation). Read only while the run is calculated or sent.
  const showResolved = CALCULATED_STATUSES.includes(run.status) || run.status === HANDED_OFF;
  useEffect(() => {
    if (loading || !showResolved) return;
    const controller = new AbortController();
    customPayRunGateway
      .getPreview(run.id, controller.signal)
      .then((preview) => {
        if (controller.signal.aborted) return;
        setResolvedByEmployee(Object.fromEntries(preview.employees.map((e) => [e.employeeId, e.earningDetails ?? []])));
      })
      .catch(() => { if (!controller.signal.aborted) setResolvedByEmployee({}); });
    return () => controller.abort();
  }, [run.id, run.status, run.updatedAtUtc, loading, showResolved]);

  // A refused service line takes focus so it is announced and read before anything else.
  useEffect(() => {
    if (blocked) blockedRef.current?.focus();
  }, [blocked]);

  const employeesById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const filtered = useMemo(
    () => employees.filter((e) => `${e.firstName} ${e.lastName} ${e.employeeNumber}`.toLowerCase().includes(search.trim().toLowerCase())),
    [employees, search],
  );
  const includedIds = useMemo(() => [...selections.values()].filter((s) => s.isIncluded).map((s) => s.employeeId), [selections]);
  // EPN-240: employees ticked for this run who are also in another live run for the same period or pay day. Checked live against the CURRENT (possibly
  // unsaved) ticks, so the flag appears as soon as the employee is selected. A warning only: the acknowledgement is asked for when sending for approval.
  const includedKey = useMemo(() => [...includedIds].sort().join(','), [includedIds]);
  useEffect(() => {
    if (readOnly || loading) return;
    if (includedKey === '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDuplicateFlags([]);
      return;
    }
    const controller = new AbortController();
    customPayRunGateway
      .checkDuplicates(run.id, includedKey.split(','), controller.signal)
      .then((flags) => {
        if (!controller.signal.aborted) setDuplicateFlags(flags);
      })
      .catch(() => {
        // Context only here: the server enforces the acknowledgement when the run is sent for approval either way.
        if (!controller.signal.aborted) setDuplicateFlags([]);
      });
    return () => controller.abort();
  }, [run.id, includedKey, readOnly, loading]);
  const flaggedIds = useMemo(() => new Set(duplicateFlags.map((f) => f.employeeId)), [duplicateFlags]);

  const selected = selectedId ? employeesById.get(selectedId) : undefined;
  const selection = selectedId ? selections.get(selectedId) : undefined;
  const selectedIncluded = selection?.isIncluded ?? false;

  const selectionsDirty = useMemo(() => {
    const norm = (m: Map<string, EmployeeSelectionInput>) =>
      JSON.stringify([...m.values()].map((s) => [s.employeeId, s.isIncluded, s.acknowledgeStatusWarning]).sort());
    return norm(selections) !== norm(savedSelections);
  }, [selections, savedSelections]);

  const dirtyEarningEmployees = useMemo(
    () =>
      Object.keys({ ...earnings, ...savedEarnings }).filter(
        (id) => normalizeLines(earnings[id] ?? []) !== normalizeLines(savedEarnings[id] ?? []),
      ),
    [earnings, savedEarnings],
  );

  const dirtyDeductionEmployees = useMemo(() => {
    const ids = new Set<string>();
    for (const d of deductions) {
      const before = savedDeductions.find((s) => s.employeeId === d.employeeId && s.deductionElectionId === d.deductionElectionId);
      if (before && before.isOptedOut !== d.isOptedOut) ids.add(d.employeeId);
    }
    return [...ids];
  }, [deductions, savedDeductions]);

  const memoDirty = memo.trim() !== savedMemo.trim();
  const dirty = selectionsDirty || memoDirty || dirtyEarningEmployees.length > 0 || dirtyDeductionEmployees.length > 0;

  // EPN-242: bulk selection. Only rows currently shown by the search can be selected, so "select all" and the count always match what the person sees.
  const filteredIds = useMemo(() => filtered.map((e) => e.id), [filtered]);
  const visibleSelected = useMemo(() => filteredIds.filter((id) => bulkSelected.has(id)), [filteredIds, bulkSelected]);
  const allShownSelected = filteredIds.length > 0 && visibleSelected.length === filteredIds.length;

  function setSelection(employeeId: string, patch: Partial<EmployeeSelectionInput>) {
    setSelections((prev) => {
      const next = new Map(prev);
      const existing = next.get(employeeId) ?? { employeeId, isIncluded: false, acknowledgeStatusWarning: false };
      next.set(employeeId, { ...existing, ...patch });
      return next;
    });
    setNotice(null);
  }

  function toggleBulk(employeeId: string) {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
    setBulkNotice(null);
  }

  function toggleAllShown() {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (allShownSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
    setBulkNotice(null);
  }

  function changeSearch(value: string) {
    setSearch(value);
    // A row that the new search hides leaves the selection, so a bulk action never touches someone the person cannot see.
    const needle = value.trim().toLowerCase();
    const stillShown = new Set(employees.filter((e) => `${e.firstName} ${e.lastName} ${e.employeeNumber}`.toLowerCase().includes(needle)).map((e) => e.id));
    setBulkSelected((prev) => new Set([...prev].filter((id) => stillShown.has(id))));
    setBulkNotice(null);
  }

  /** Include / Exclude selected: the same draft change as ticking each row, saved by the same Save / Proceed. An employee who is not Active is never included here:
   * that needs the per-employee "Include anyway" confirmation, which is asked for on the employee's own details. */
  function applyBulk(include: boolean) {
    if (visibleSelected.length === 0) return;
    const next = new Map(selections);
    let changed = 0;
    const skipped: string[] = [];
    for (const id of visibleSelected) {
      const e = employeesById.get(id);
      if (!e) continue;
      if (include && e.status !== 0) {
        skipped.push(fullName(e));
        continue;
      }
      const existing = next.get(id) ?? { employeeId: id, isIncluded: false, acknowledgeStatusWarning: false };
      if (existing.isIncluded !== include) changed++;
      next.set(id, { ...existing, isIncluded: include });
    }
    setSelections(next);
    setNotice(null);
    setBulkSelected(new Set());
    const parts = [`${include ? 'Included' : 'Excluded'} ${changed} employee${changed === 1 ? '' : 's'}. Not saved yet: use Save and Confirm All Changes.`];
    if (skipped.length > 0) {
      parts.push(
        `${skipped.length} selected employee${skipped.length === 1 ? ' is' : 's are'} not Active and ${skipped.length === 1 ? 'was' : 'were'} not included (${skipped.join(', ')}). ` +
          'Select each one in the Employee List and tick "Include anyway" to include them.',
      );
    }
    if (include) parts.push('Pay method readiness is checked in Review Payroll, as for any included employee.');
    setBulkNotice(parts.join(' '));
  }

  function saveLine(employeeId: string, line: DraftLine) {
    setEarnings((prev) => {
      const own = prev[employeeId] ?? [];
      const exists = own.some((l) => l.key === line.key);
      return { ...prev, [employeeId]: exists ? own.map((l) => (l.key === line.key ? line : l)) : [...own, line] };
    });
    setEditingKey(null);
    setNotice(null);
  }

  function removeLine(employeeId: string, key: string) {
    setEarnings((prev) => ({ ...prev, [employeeId]: (prev[employeeId] ?? []).filter((l) => l.key !== key) }));
    setNotice(null);
  }

  function toggleDeduction(row: DeductionElectionWithOptOutResponse) {
    setDeductions((prev) =>
      prev.map((d) =>
        d.employeeId === row.employeeId && d.deductionElectionId === row.deductionElectionId ? { ...d, isOptedOut: !d.isOptedOut } : d,
      ),
    );
    setNotice(null);
  }

  /** Everything the server would refuse or that would make a calculation meaningless, found before anything is sent. */
  function validate(): string[] {
    const found: string[] = [];
    if (includedIds.length === 0) found.push('Include at least one employee in this run.');
    for (const id of includedIds) {
      const e = employeesById.get(id);
      if (!e) continue;
      const s = selections.get(id);
      if (e.status !== 0 && !s?.acknowledgeStatusWarning) {
        found.push(
          `${fullName(e)} is ${employmentStatusLabels[e.status] ?? 'not Active'} — select ${fullName(e)} in the Employee List and tick "Include anyway" in their details, or untick them to remove them from the run.`,
        );
      }
      for (const l of earnings[id] ?? []) {
        if (l.serviceLine) {
          // A service line is priced by the server from the rate schedule: it needs a code, a discipline and a quantity, and never a rate.
          if (!l.earningCodeId || !l.serviceLine.disciplineId || !(l.serviceLine.quantity > 0)) {
            found.push(`A service line for ${fullName(e)} is incomplete: choose an earning code, a worker category and a quantity.`);
          }
          continue;
        }
        const hasFlat = l.flatAmount != null;
        const hasHours = l.hours != null && l.rate != null;
        const values = [l.flatAmount, l.hours, l.rate].filter((v): v is number => v != null);
        if (!l.earningCodeId || (!hasFlat && !hasHours) || values.some((v) => !Number.isFinite(v) || v < 0)) {
          found.push(`An earning line for ${fullName(e)} is incomplete — choose an earning code and enter a flat amount, or hours and a rate.`);
        }
      }
    }
    return found;
  }

  /** Opens the details of the first included employee who still needs "Include anyway", so the checkbox the message names is on screen. */
  function showFirstUnacknowledged() {
    const id = includedIds.find((i) => {
      const e = employeesById.get(i);
      return e && e.status !== 0 && !selections.get(i)?.acknowledgeStatusWarning;
    });
    if (id) setSelectedId(id);
  }

  /** Writes every pending change through the existing endpoints, in the order the server needs (selection first, since
   * earnings and opt-outs are only accepted for included employees), then re-reads the saved state. */
  async function persist(): Promise<boolean> {
    if (memoDirty) {
      const saved = await customPayRunGateway.setMemo(run.id, memo.trim() === '' ? null : memo);
      setSavedMemo(saved.description ?? '');
      setMemo(saved.description ?? '');
    }
    if (selectionsDirty) {
      await customPayRunGateway.setEmployees(run.id, [...selections.values()]);
    }
    for (const id of dirtyEarningEmployees) {
      if (!selections.get(id)?.isIncluded) continue;
      const lines: EarningLineInput[] = (earnings[id] ?? []).map(({ key: _key, ...rest }) => rest);
      await customPayRunGateway.setEarnings(run.id, id, lines);
    }
    for (const id of dirtyDeductionEmployees) {
      if (!selections.get(id)?.isIncluded) continue;
      await customPayRunGateway.setDeductionOptOuts(
        run.id,
        id,
        deductions
          .filter((d) => d.employeeId === id)
          .map((d) => ({ deductionElectionId: d.deductionElectionId, isOptedOut: d.isOptedOut, reason: d.reason })),
      );
    }
    await load();
    await refreshRun();
    setBulkNotice(null); // the "Not saved yet" bulk message no longer applies once everything is saved
    return true;
  }

  /** A refused service line gets its own plain-language explanation; any other failure keeps the generic message. */
  function reportFailure(err: unknown, fallback: string) {
    const problem = describeServiceLineProblem(err);
    setBlocked(problem);
    setError(problem ? null : friendlyErrorMessage(err, fallback));
  }

  async function handleSave() {
    setNotice(null);
    setError(null);
    setBlocked(null);
    const found = validate();
    setProblems(found);
    if (found.length > 0) {
      showFirstUnacknowledged();
      return;
    }
    setBusy('save');
    try {
      await persist();
      setNotice('All changes are saved.');
    } catch (err) {
      reportFailure(err, 'Could not save these changes — a non-Active employee may need acknowledgment.');
    } finally {
      setBusy(null);
    }
  }

  async function handleProceed() {
    setNotice(null);
    setError(null);
    setBlocked(null);
    const found = validate();
    setProblems(found);
    if (found.length > 0) {
      showFirstUnacknowledged();
      return;
    }
    setBusy('proceed');
    try {
      if (dirty) await persist();
      // Nothing changed since the last calculation: keep it rather than recalculating (a recalculation is a new calculation).
      // One idempotency key per click: a retry of this same request reuses its calculations; the next click is a deliberate new calculation.
      if (dirty || !CALCULATED_STATUSES.includes(run.status)) {
        const result = await customPayRunGateway.calculate(run.id, crypto.randomUUID());
        // The server's coded refusal of a service line (no rate, ambiguous rates, a discipline not held, double payment) comes back as that employee's exception, with
        // the code first. Stay here and say why and how to fix it, instead of moving on to a review that cannot pay the line.
        const refused = result.calculations
          .map((c) => ({ c, problem: describeExceptionProblem(c.exceptionFlags) }))
          .find((x): x is { c: typeof x.c; problem: ServiceLineProblem } => x.problem !== null);
        if (refused) {
          await refreshRun();
          const name = employeesById.get(refused.c.employeeId);
          setBlocked({ ...refused.problem, detail: name ? `${fullName(name)}: ${refused.problem.detail}` : refused.problem.detail });
          return;
        }
      }
      await refreshRun();
      goToStep('review-payroll');
    } catch (err) {
      reportFailure(err, 'Could not calculate this run.');
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;

  const ownLines = selectedId ? (earnings[selectedId] ?? []) : [];
  const ownDeductions = selectedId ? deductions.filter((d) => d.employeeId === selectedId) : [];
  const roleName = jobTitles.find((t) => t.id === selected?.jobTitleId)?.name ?? '—';
  const branch = (selectedId && branchByEmployee[selectedId]) || '—';
  const rateState = selectedId ? rateByEmployee[selectedId] : undefined;
  const assignment = rateState?.status === 'ready' ? rateState.assignment : null;
  const template = assignment?.workRateId ? workRates.find((w) => w.id === assignment.workRateId) : undefined;
  const basisText = template ? ` (${(workRateAmountBasisLabels[template.amountBasis] ?? '').toLowerCase()})` : '';
  const payTypeText = !rateState || rateState.status === 'loading' ? 'Loading…' : rateState.status === 'error' ? 'Pay rate unavailable' : assignment ? (compensationPayTypeLabels[assignment.payType] ?? '—') : 'No pay rate saved';
  const rateText = assignment ? `Saved rate ${money(assignment.rate)}${assignment.payType === 1 ? ' per hour' : basisText}` : '—';
  const deductionName = (id: string) => deductionCodes.find((c) => c.id === id)?.name ?? id;
  /** The i-th service line's price, only while the saved lines are the ones that were calculated (no unsaved change for this employee). */
  const serviceDetailFor = (employeeId: string, lines: DraftLine[], index: number): EarningDetail | undefined => {
    if (!showResolved || dirtyEarningEmployees.includes(employeeId)) return undefined;
    const serviceIndex = lines.slice(0, index + 1).filter((x) => x.serviceLine).length - 1;
    const detail = (resolvedByEmployee[employeeId] ?? []).filter((d) => d.disciplineId)[serviceIndex];
    return detail && detail.disciplineId === lines[index]?.serviceLine?.disciplineId ? detail : undefined;
  };
  const savedIncluded = selectedId ? (savedSelections.get(selectedId)?.isIncluded ?? false) : false;
  const controlsOff = readOnly || busy !== null;
  // EPN-242: the bulk and selection controls are off after hand-off, discard or withdrawal and for a user without custompayruns.write.
  const canEdit = !readOnly && canWrite;
  // The server names the other run's status by its internal name ("status HandedOffToPayrollReview"); show the plain-language label instead.
  const runLevelWarnings = run.duplicateWarnings
    .filter((w) => w.startsWith('Custom pay run '))
    .map((w) => w.replace(/status (\w+)\)/, (_m, status: string) => `${runStatusLabel(status).toLowerCase()})`));

  return (
    <div>
      {readOnly && (
        <p className="cpr-notice cpr-notice--warning" role="status">
          {discarded
            ? 'This run was discarded. It is kept in the run history and can no longer be modified.'
            : withdrawn
              ? 'This run was withdrawn by the preparer after it was sent. It is kept in the run history and can no longer be modified.'
              : 'This run has been sent for approval and can no longer be modified here.'}
        </p>
      )}

      {isBonus && !readOnly && (
        <p className="cpr-notice" role="note" data-testid="bonus-notice">
          <strong>Bonus run.</strong> It starts empty: an employee&apos;s regular salary or hourly pay is not included. Only the earnings you add here are paid. Tick the
          employees you want, then add an earning for each (an employee with no earning is not paid).
        </p>
      )}
      <div className="cpr-fields">
        <label className="cpr-field">
          Business Pay Schedule
          <select disabled value={schedule?.id ?? ''} aria-describedby="cpr-fixed-note">
            {schedule ? (
              schedules.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))
            ) : (
              <option value="">No pay schedule linked</option>
            )}
          </select>
        </label>
        <label className="cpr-field">
          Pay Schedule Period
          <input readOnly value={frequencyLabel(schedule)} aria-describedby="cpr-fixed-note" />
        </label>
        <label className="cpr-field">
          Pay Day
          <input readOnly value={formatUsDate(run.checkDate)} aria-describedby="cpr-fixed-note" />
        </label>
        <label className="cpr-field">
          Select Pay Period
          <input readOnly value={formatUsPeriod(run)} aria-describedby="cpr-fixed-note" />
        </label>
        <div className="cpr-fields__action">
          <button type="button" className="cpr-btn" disabled title="Bulk paystub generation is not yet connected">
            Bulk Generate Paystubs
          </button>
          <NotConnected />
        </div>
      </div>
      <p id="cpr-fixed-note" className="muted">
        The pay schedule, pay day and pay period are fixed when a run is created. To use different dates, start a new Custom Pay Run.
      </p>
      {/* The server's warnings: the run-level one ("Custom pay run ... already covers this exact period and check date") and, since EPN-240, one line per
          employee. The employee lines are shown below from the live check (which follows unsaved ticks), so only the run-level lines are listed here. */}
      {runLevelWarnings.length > 0 && !readOnly && (
        <div className="cpr-notice cpr-notice--warning" role="status">
          <strong>Possible duplicate run</strong>
          <ul>
            {runLevelWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      {!readOnly && <DuplicateFlagsNotice flags={duplicateFlags} />}

      {error && (
        <p className="cpr-notice cpr-notice--error" role="alert">
          {error}
        </p>
      )}
      {blocked && (
        <div ref={blockedRef} className="hdc-problem" role="alert" tabIndex={-1} data-testid="service-line-problem" data-code={blocked.code}>
          <p className="hdc-problem__title"><span aria-hidden="true">{'⚠'} </span>{blocked.title}</p>
          {blocked.detail && <p>{blocked.detail}</p>}
          <p><strong>How to fix it:</strong> {blocked.fix}</p>
          {(blocked.code === HdcErrorCode.RateNotFound || blocked.code === HdcErrorCode.RateAmbiguous) && (
            <p><Link to="/settings/compensation-rates">Open Compensation Rates</Link></p>
          )}
          <p className="muted">Nothing was paid or sent. The run stays as it was, and you can change the line and try again.</p>
        </div>
      )}
      {problems.length > 0 && (
        <div className="cpr-notice cpr-notice--error" role="alert">
          <strong>Fix these before continuing:</strong>
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {notice && (
        <p className="cpr-notice cpr-notice--success" role="status">
          {notice}
        </p>
      )}

      <div className="cpr-split">
        <section className="cpr-list" aria-label="Employee List">
          <h3>Employee List</h3>
          <input
            className="search-box"
            type="search"
            placeholder="Search Name"
            aria-label="Search employees"
            value={search}
            onChange={(e) => changeSearch(e.target.value)}
          />
          {employees.length === 0 && <p className="muted">No employees found.</p>}
          {employees.length > 0 && filtered.length === 0 && <p className="muted">No employee matches that search.</p>}
          {filtered.length > 0 && (
            <div className="cpr-bulk" role="group" aria-label="Bulk actions">
              <label className="cpr-bulk__all">
                <input
                  type="checkbox"
                  checked={allShownSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = visibleSelected.length > 0 && !allShownSelected;
                  }}
                  disabled={!canEdit || busy !== null}
                  onChange={toggleAllShown}
                />
                <span>Select all shown</span>
              </label>
              <span className="cpr-bulk__count" aria-live="polite" data-testid="bulk-count">
                {visibleSelected.length} selected
              </span>
              <div className="cpr-bulk__actions">
                <button type="button" className="cpr-btn cpr-btn--outline" disabled={!canEdit || busy !== null || visibleSelected.length === 0} onClick={() => applyBulk(true)}>
                  Include selected
                </button>
                <button type="button" className="cpr-btn cpr-btn--outline" disabled={!canEdit || busy !== null || visibleSelected.length === 0} onClick={() => applyBulk(false)}>
                  Exclude selected
                </button>
              </div>
              {!readOnly && !canWrite && <p className="muted">{NO_PERMISSION_NOTE}</p>}
              {bulkNotice && (
                <p className="cpr-notice" role="status" data-testid="bulk-notice">
                  {bulkNotice}
                </p>
              )}
            </div>
          )}
          {filtered.length > 0 && (
            <div className="cpr-list__legend" aria-hidden="true">
              <span>Select</span>
              <span>In run</span>
            </div>
          )}
          <ul className="cpr-list__items">
            {filtered.map((e) => {
              const s = selections.get(e.id);
              return (
                <li key={e.id} className={`cpr-list__item${e.id === selectedId ? ' cpr-list__item--selected' : ''}`}>
                  <input
                    type="checkbox"
                    className="cpr-list__select"
                    aria-label={`Select ${fullName(e)} for a bulk action`}
                    checked={bulkSelected.has(e.id)}
                    disabled={!canEdit || busy !== null}
                    onChange={() => toggleBulk(e.id)}
                  />
                  <input
                    type="checkbox"
                    aria-label={`Include ${fullName(e)} in this run`}
                    checked={s?.isIncluded ?? false}
                    disabled={controlsOff || !canWrite}
                    onChange={(ev) => setSelection(e.id, { isIncluded: ev.target.checked })}
                  />
                  <button type="button" className="cpr-list__name" aria-pressed={e.id === selectedId} onClick={() => setSelectedId(e.id)}>
                    {fullName(e)}
                  </button>
                  {e.status !== 0 && <span className="badge badge--warning">{employmentStatusLabels[e.status] ?? 'Not Active'}</span>}
                  {flaggedIds.has(e.id) && (s?.isIncluded ?? false) && (
                    <span className="badge badge--warning" title="Also in another run for this period or pay day">
                      In another run
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="muted">{includedIds.length} included</p>
        </section>

        <section className="cpr-detail" aria-label="Selected employee">
          {!selected && <p className="muted">Select an employee to review their earnings for this run.</p>}
          {selected && (
            <>
              <h3>{fullName(selected)}</h3>

              {!selectedIncluded && (
                <p className="cpr-notice" role="status">
                  Not included in this run. Tick the checkbox next to their name to include them.
                </p>
              )}
              {selectedIncluded && selected.status !== 0 && (
                <label className="cpr-ack">
                  <input
                    type="checkbox"
                    checked={selection?.acknowledgeStatusWarning ?? false}
                    disabled={controlsOff}
                    onChange={(ev) => setSelection(selected.id, { acknowledgeStatusWarning: ev.target.checked })}
                  />
                  <span>
                    Include anyway — {fullName(selected)} is {employmentStatusLabels[selected.status] ?? 'not Active'}.
                  </span>
                </label>
              )}

              <p className="muted" data-testid="rate-note">
                The saved pay rate is shown for reference as of the pay day. Gross Pay for the saved rate is worked out when the run is calculated
                (Review Payroll); entering hours or a prefilled salary here is awaiting payroll confirmation, so only custom earnings below are
                added to the run.
              </p>
              <div className="cpr-detail__scroll">
                <table aria-label="Earnings">
                  <thead>
                    <tr>
                      <th scope="col">Branch</th>
                      <th scope="col">Category/Role</th>
                      <th scope="col">Pay Type</th>
                      <th scope="col">Earnings</th>
                      <th scope="col" className="num">
                        Gross Pay
                      </th>
                      <th scope="col">
                        <span className="cpr-sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr data-testid="profile-row">
                      <td>{branch}</td>
                      <td>{roleName}</td>
                      <td>{payTypeText}</td>
                      <td>{rateText}</td>
                      <td className="num">—</td>
                      <td />
                    </tr>
                    {ownLines.length === 0 && (
                      <tr>
                        <td colSpan={6} className="muted">
                          No custom earnings added for this run. Earnings added here apply to this run only.
                        </td>
                      </tr>
                    )}
                    {ownLines.map((l, lineIndex) =>
                      editingKey === l.key ? (
                        <tr key={l.key}>
                          <td colSpan={6}>
                            <EarningLineForm
                              codes={earningCodes}
                              initial={l}
                              submitLabel="Update earning"
                              service={{ employeeId: selected.id, periodStart: run.periodStartDate, periodEnd: run.periodEndDate, locations }}
                              onSubmit={(line) => saveLine(selected.id, line)}
                              onCancel={() => setEditingKey(null)}
                            />
                          </td>
                        </tr>
                      ) : (
                        <Fragment key={l.key}>
                        <tr>
                          <td>{branch}</td>
                          <td>{l.serviceLine ? (l.serviceLine.disciplineName ?? disciplineNames.get(l.serviceLine.disciplineId) ?? serviceDetailFor(selected.id, ownLines, lineIndex)?.disciplineName ?? roleName) : roleName}</td>
                          <td>{earningCodes.find((c) => c.id === l.earningCodeId)?.name ?? l.label}</td>
                          <td>{describeEarningLine(l, serviceTypeNames)}</td>
                          <td className="num">{l.serviceLine ? (serviceDetailFor(selected.id, ownLines, lineIndex)?.amount !== undefined ? money(serviceDetailFor(selected.id, ownLines, lineIndex)!.amount) : '—') : money(earningLineAmount(l))}</td>
                          <td>
                            <div className="cpr-actions">
                              <button
                                type="button"
                                className="cpr-btn cpr-btn--outline"
                                disabled={controlsOff}
                                aria-label={`Edit earning ${l.label}`}
                                onClick={() => setEditingKey(l.key)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="cpr-btn cpr-btn--outline"
                                disabled={controlsOff}
                                aria-label={`Remove earning ${l.label}`}
                                onClick={() => removeLine(selected.id, l.key)}
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                        </Fragment>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
              {ownLines.map((l, lineIndex) => (l.serviceLine ? <ServiceLinePreview key={l.key} line={l} detail={serviceDetailFor(selected.id, ownLines, lineIndex)} /> : null))}

              {editingKey === 'new' ? (
                <EarningLineForm
                  codes={earningCodes}
                  initial={null}
                  submitLabel="Add earning"
                  service={{ employeeId: selected.id, periodStart: run.periodStartDate, periodEnd: run.periodEndDate, locations }}
                  onSubmit={(line) => saveLine(selected.id, line)}
                  onCancel={() => setEditingKey(null)}
                />
              ) : (
                <p>
                  <button
                    type="button"
                    className="cpr-btn cpr-btn--outline"
                    disabled={controlsOff || !selectedIncluded}
                    onClick={() => setEditingKey('new')}
                  >
                    + Add Custom Earning
                  </button>
                  {!selectedIncluded && <span className="muted"> Include this employee first.</span>}
                </p>
              )}

              <h4>Deductions for this run</h4>
              {!savedIncluded && selectedIncluded && (
                <p className="muted">Save changes to load this employee&apos;s recurring deductions.</p>
              )}
              {savedIncluded && ownDeductions.length === 0 && <p className="muted">No active recurring deductions for this employee.</p>}
              {ownDeductions.length > 0 && (
                <table aria-label="Deductions">
                  <thead>
                    <tr>
                      <th scope="col">Deduction Code</th>
                      <th scope="col">Amount Override</th>
                      <th scope="col">Applies to this run?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownDeductions.map((d) => (
                      <tr key={d.deductionElectionId}>
                        <td>{deductionName(d.deductionCodeId)}</td>
                        <td>{d.amountOverride ?? '—'}</td>
                        <td>
                          <label>
                            <input type="checkbox" checked={!d.isOptedOut} disabled={controlsOff} onChange={() => toggleDeduction(d)} /> Apply
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="muted">Opting out affects this run only; the recurring deduction itself is never changed.</p>

              <div className="cpr-lower">
                <div className="cpr-lower__block">
                  <h4>Add Leaves</h4>
                  <select disabled aria-label="Add Leaves (not yet connected)" defaultValue="">
                    <option value="">Select Option</option>
                  </select>
                  <NotConnected />
                </div>
                <div className="cpr-lower__block">
                  <h4>Add Reimbursements/Mileage</h4>
                  <button type="button" className="cpr-btn cpr-btn--outline" disabled title="Reimbursements and mileage are not yet connected">
                    + Add Item
                  </button>
                  <NotConnected />
                </div>
              </div>

              <div className="cpr-memo">
                <label>
                  <input type="checkbox" disabled /> Generate Paystub
                </label>
                <NotConnected />
              </div>
            </>
          )}
        </section>
      </div>

      <section className="cpr-memo-run" aria-label="Memo">
        <label htmlFor="cpr-memo-input">Memo</label>
        <textarea
          id="cpr-memo-input"
          rows={2}
          maxLength={MEMO_MAX_LENGTH}
          value={memo}
          readOnly={!canEdit}
          disabled={busy !== null}
          placeholder={canEdit ? '+ Add Memo Here' : 'No memo.'}
          aria-describedby="cpr-memo-help"
          onChange={(e) => {
            setMemo(e.target.value);
            setNotice(null);
          }}
        />
        <p id="cpr-memo-help" className="muted">
          {readOnly
            ? 'The memo can no longer be changed once the run is sent, discarded or withdrawn.'
            : !canWrite
              ? NO_PERMISSION_NOTE
              : 'A note for the approver, saved with this run. It is not part of any pay calculation and is not in the CSV export.'}{' '}
          <span data-testid="memo-count">
            {memo.length} / {MEMO_MAX_LENGTH}
          </span>
        </p>
      </section>

      <div className="cpr-footer">
        <span className="muted">{dirty ? 'You have unsaved changes.' : 'No unsaved changes.'}</span>
        <div className="cpr-footer__group">
          <button type="button" className="cpr-btn" disabled={readOnly || busy !== null} onClick={() => void handleSave()}>
            {busy === 'save' ? 'Saving…' : 'Save and Confirm All Changes'}
          </button>
          {readOnly ? (
            <button type="button" className="cpr-btn cpr-btn--green" onClick={() => goToStep('review-payroll')}>
              Proceed to Review Payroll
            </button>
          ) : (
            <button type="button" className="cpr-btn cpr-btn--green" disabled={busy !== null} onClick={() => void handleProceed()}>
              {busy === 'proceed' ? 'Calculating…' : 'Proceed to Review Payroll'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** How a new line is priced. Wording avoids "rate" so the existing field labels stay unambiguous. */
function PricingChoice({ kind, onChange }: { kind: 'standard' | 'service'; onChange: (kind: 'standard' | 'service') => void }) {
  return (
    <div role="radiogroup" aria-label="How this earning is priced" className="cpr-pricing">
      <label>
        <input type="radio" name="cpr-pricing" checked={kind === 'standard'} onChange={() => onChange('standard')} />
        <span>Standard earning (flat amount, or hours and an amount per hour)</span>
      </label>
      <label>
        <input type="radio" name="cpr-pricing" checked={kind === 'service'} onChange={() => onChange('service')} />
        <span>Service line (worker category, work type and quantity)</span>
      </label>
    </div>
  );
}

/** The read-only price under a service line: set by EPN, never typed, with where it came from and the server's own explanation. */
function ServiceLinePreview({ line, detail }: { line: DraftLine; detail: EarningDetail | undefined }) {
  const s = line.serviceLine!;
  return (
    <div className="cpr-line-preview" data-testid="service-line-preview">
      <p className="cpr-line-preview__title"><strong>{line.label}</strong></p>
      {detail ? (
        <dl aria-label={`Resolved rate for ${line.label}`}>
          <dt>Rate (read only)</dt>
          <dd>
            {formatWireMoney(detail.effectiveRate ?? detail.rate)}
            {detail.rateSource && <> from {rateSourceLabels[detail.rateSource].toLowerCase()}</>}
          </dd>
          <dt>How it was worked out</dt>
          <dd>{detail.explanation}</dd>
          {detail.workedHours != null && (<><dt>Worked hours kept</dt><dd>{detail.workedHours}</dd></>)}
          {healthcareRatesGateway.isSynthetic && (<><dt>{SYNTHETIC_RATES_NOTICE_TITLE}</dt><dd>{SYNTHETIC_RATES_NOTICE}</dd></>)}
        </dl>
      ) : (
        <span className="muted">
          Rate: set by EPN when the run is calculated, from your rate schedule; it is never typed here.
          {s.additionalEarningAuthorized && ' Marked as an additional earning.'} Choose Proceed to Review Payroll to price this line.
        </span>
      )}
    </div>
  );
}

interface FormProps {
  codes: EarningCodeResponse[];
  initial: DraftLine | null;
  submitLabel: string;
  /** What a service line needs: the employee, the run's pay period and the company's locations. */
  service: { employeeId: string; periodStart: string; periodEnd: string; locations: CompanyWorkLocationOption[] };
  onSubmit: (line: DraftLine) => void;
  onCancel: () => void;
}

/** One earning line. Every line references a real, active earning code — never free text — and is either a flat amount or
 * hours × rate, the same rule the server enforces. Run-scoped only: nothing here touches recurring compensation. */
function EarningLineForm({ codes, initial, submitLabel, service, onSubmit, onCancel }: FormProps) {
  // A line is either a standard earning (a flat amount, or hours and a rate) or a service line (a quantity priced from the rate schedule). The choice is only offered
  // when adding; an existing line keeps its kind.
  const [kind, setKind] = useState<'standard' | 'service'>(initial?.serviceLine ? 'service' : 'standard');
  const [codeId, setCodeId] = useState(initial?.earningCodeId ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [hours, setHours] = useState(initial?.hours != null ? String(initial.hours) : '');
  const [rate, setRate] = useState(initial?.rate != null ? String(initial.rate) : '');
  const [flat, setFlat] = useState(initial?.flatAmount != null ? String(initial.flatAmount) : '');
  const [problem, setProblem] = useState<string | null>(null);

  function submit() {
    const h = hours === '' ? null : Number(hours);
    const r = rate === '' ? null : Number(rate);
    const f = flat === '' ? null : Number(flat);
    const numbers = [h, r, f].filter((v): v is number => v !== null);
    if (!codeId) return setProblem('Choose an earning code.');
    if (numbers.some((v) => !Number.isFinite(v) || v < 0)) return setProblem('Amounts must be zero or more.');
    if (f === null && (h === null || r === null)) return setProblem('Enter a flat amount, or both hours and a rate.');
    const code = codes.find((c) => c.id === codeId);
    setProblem(null);
    onSubmit({
      key: initial?.key ?? crypto.randomUUID(),
      earningCodeId: codeId,
      label: label.trim() || code?.name || 'Custom earning',
      hours: f === null ? h : null,
      rate: f === null ? r : null,
      flatAmount: f,
    });
  }

  if (kind === 'service') {
    return (
      <div>
        {!initial && <PricingChoice kind={kind} onChange={setKind} />}
        <ServiceLineForm
          employeeId={service.employeeId}
          periodStart={service.periodStart}
          periodEnd={service.periodEnd}
          codes={codes}
          locations={service.locations}
          initial={initial}
          submitLabel={submitLabel}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      </div>
    );
  }

  return (
    <div className="cpr-earning-form" role="group" aria-label={initial ? 'Edit earning' : 'Add custom earning'}>
      {!initial && <PricingChoice kind={kind} onChange={setKind} />}
      <label>
        Earning code
        <select value={codeId} onChange={(e) => setCodeId(e.target.value)}>
          <option value="">Choose…</option>
          {codes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Label (optional)
        <input value={label} onChange={(e) => setLabel(e.target.value)} />
      </label>
      <label>
        Hours
        <input type="number" min="0" step="0.01" value={hours} disabled={flat !== ''} onChange={(e) => setHours(e.target.value)} />
      </label>
      <label>
        Rate
        <input type="number" min="0" step="0.01" value={rate} disabled={flat !== ''} onChange={(e) => setRate(e.target.value)} />
      </label>
      <label>
        Flat amount
        <input
          type="number"
          min="0"
          step="0.01"
          value={flat}
          disabled={hours !== '' || rate !== ''}
          onChange={(e) => setFlat(e.target.value)}
        />
      </label>
      <button type="button" className="cpr-btn" onClick={submit}>
        {submitLabel}
      </button>
      <button type="button" className="cpr-btn cpr-btn--ghost" onClick={onCancel}>
        Cancel
      </button>
      {problem && (
        <p className="error" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
